/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import config from "../config/config";
import FormControl from "./Common/FormControl";
import FormLabel from "./Common/FormLabel";
import ToastNotification from "./Common/ToastNotification";
import { CONSTANTS } from "../utils/Constant/Constant";
import { useNavigate } from "react-router-dom";
import appInsights, { setAppInsightsUserContext } from "../AppInsights"; // ✅ import helper

const resetPasswordConfig = {
  authority: `${config.MSAL_FORGOT_PASSWORD}`,
  clientId: config.MSAL_CLIENT_ID,
  redirectUri: config.MSAL_REDIRECT_URI,
  scopes: ["openid", "profile", "email", "offline_access"],
  knownAuthorities: [`${config.MSAL_AUTHORITY_AUTHORITIES}`],
};

const Login = ({ login }) => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    username: "",
    password: "",
    global: "",
  });
  const [toastData, setToastData] = useState(null);

  // Refs for time tracking
  const pageLoadTime = useRef(Date.now());
  const timeTrackingInterval = useRef(null);
  const totalTimeMinutes = useRef(0);

  // Track total time spent on login page in minutes
  const trackLoginPageTimeSpent = () => {
    const finalMinutes = getCurrentTimeSpentMinutes();
    
    appInsights.trackEvent({
      name: "LoginPage_Time_Spent",
      properties: {
        username: formData.username || "not_entered",
        totalTimeMinutes: finalMinutes,
        totalTimeFormatted: `${finalMinutes} minutes`,
        loginAttempts: getLoginAttempts(),
        passwordVisibilityToggled: showPassword,
        forgotPasswordClicked: false,
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        pageExitTimestamp: new Date().toISOString(),
        sessionOutcome: getSessionOutcome(),
        fieldInteractions: getFieldInteractionSummary(),
      },
    });
  };

  // Helper to get current time spent in minutes
  const getCurrentTimeSpentMinutes = () => {
    const seconds = (Date.now() - pageLoadTime.current) / 1000;
    return Math.floor(seconds / 60);
  };

  // Set up time tracking interval
  const setupTimeTrackingInterval = () => {
    timeTrackingInterval.current = setInterval(() => {
      totalTimeMinutes.current += 1; // Add 1 minute each interval
      trackTimeSpentEvent("LoginPage_Time_Update", totalTimeMinutes.current);
    }, 60000); // Every 60 seconds
  };

  // Generic time tracking function for login page
  const trackTimeSpentEvent = (eventName, minutes) => {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        username: formData.username || "not_entered",
        totalTimeMinutes: minutes,
        totalTimeFormatted: `${minutes} minutes`,
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        eventTimestamp: new Date().toISOString(),
        fieldInteractions: getFieldInteractionSummary(),
      },
    });
  };

  // Helper function to track login attempts during session
  const getLoginAttempts = () => {
    return loading ? 1 : 0;
  };

  // Helper function to determine session outcome
  const getSessionOutcome = () => {
    if (loading) return "login_in_progress";
    if (toastData?.type === CONSTANTS.TOAST_SUCCESS) return "login_successful";
    if (toastData?.type === CONSTANTS.TOAST_ERROR) return "login_failed";
    return "abandoned";
  };

  // Helper function to track field interactions
  const getFieldInteractionSummary = () => {
    return {
      usernameEntered: !!formData.username,
      passwordEntered: !!formData.password,
      bothFieldsFilled: !!formData.username && !!formData.password,
    };
  };

  // Track page unload and visibility changes
  useEffect(() => {
    // Track page load
    appInsights.trackEvent({
      name: "LoginPage_Loaded",
      properties: {
        timestamp: new Date().toISOString(),
        hasStoredUsername: !!localStorage.getItem("userName"),
      },
    });

    // Set up time tracking interval
    setupTimeTrackingInterval();

    // Track beforeunload (when user closes tab/browser)
    const handleBeforeUnload = () => {
      trackLoginPageTimeSpent();
    };

    // Track visibility change (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTimeSpentEvent("LoginPage_Hidden", getCurrentTimeSpentMinutes());
      } else {
        trackTimeSpentEvent("LoginPage_Visible", getCurrentTimeSpentMinutes());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Clean up interval
      if (timeTrackingInterval.current) {
        clearInterval(timeTrackingInterval.current);
      }
      
      // Track when component unmounts (navigation to other pages)
      trackLoginPageTimeSpent();
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [formData.username, formData.password, loading, toastData, showPassword]);

  // 🔹 Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Track field interactions
    if (value && !formData[name]) {
      appInsights.trackException({
        exception: new Error(`LoginPage_Field_Started: ${name}`),
        severityLevel: 0,
        properties: {
          fieldName: name,
          username: formData.username || "not_entered",
          timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
      global: "",
    }));
  };

  // Track password visibility toggle
  const handlePasswordToggle = () => {
    appInsights.trackEvent({
      name: "LoginPage_Password_Toggled",
      properties: {
        username: formData.username || "not_entered",
        newVisibility: !showPassword,
        timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
      },
    });
    setShowPassword((prev) => !prev);
  };

  // 🔹 Azure AD B2C login (ROPC flow)
  const loginWithB2C = async (username, password) => {
    const clientId = config.MSAL_CLIENT_ID;
    const scope = config.SCOPE_USER_READWRITE_ALL;
    const policy = config.CUSTOME_POLICY_NAME;
    const tenant = config.CUSTOME_TENANT_NAME;

    const b2cloginURL = `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${policy}/oauth2/v2.0/token`;

    const body = new URLSearchParams({
      grant_type: "password",
      username,
      password,
      scope: `${scope} openid offline_access`,
      client_id: clientId,
    }).toString();

    const response = await fetch(b2cloginURL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error_description || "Invalid credentials");
    }

    if (!data.access_token) {
      throw new Error("No access token received");
    }

    return data;
  };

  // 🔹 Handle form submit
  const handleLogin = async () => {
    const loginAttemptMinutes = getCurrentTimeSpentMinutes();
    
    // Track login attempt
    appInsights.trackEvent({
      name: "LoginPage_Login_Attempted",
      properties: {
        username: formData.username,
        timeToAttempt: `${loginAttemptMinutes} minutes`,
        fieldsFilled: {
          username: !!formData.username,
          password: !!formData.password,
        },
      },
    });

    setFormErrors({ username: "", password: "", global: "" });

    // Validation
    let errors = {};
    if (!formData.username) {
      errors.username = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.username)) {
        errors.username = "Please enter a valid email address.";
      }
    }

    if (!formData.password) {
      errors.password = "Password is required.";
    }

    if (Object.keys(errors).length > 0) {
      appInsights.trackException({
        exception: new Error(`Login_ValidationFailed: ${Object.keys(errors).join(', ')}`),
        severityLevel: 1,
        properties: { 
          username: formData.username, 
          errors,
          timeToValidation: `${loginAttemptMinutes} minutes`,
        },
      });
      setFormErrors((prev) => ({ ...prev, ...errors }));
      return;
    }

    setLoading(true);
    try {
      const data = await loginWithB2C(formData.username, formData.password);

      const totalLoginMinutes = getCurrentTimeSpentMinutes();
      
      localStorage.setItem("Token", data.access_token);
      localStorage.setItem("RefreshToken", data.refresh_token || "");
      localStorage.setItem("userName", formData.username);

      // ✅ Set global Application Insights user context (username only for now)
      setAppInsightsUserContext(formData.username, "PendingCoachId");

      // 🔹 Track success with user identity and time metrics
      appInsights.trackEvent({
        name: "Login_Success",
        properties: { 
          username: formData.username,
          totalLoginTime: `${totalLoginMinutes} minutes`,
          timeToSuccess: `${totalLoginMinutes} minutes`,
        },
      });

      // Track successful login time before navigation
      trackLoginPageTimeSpent();
      
      login();
      navigate("/dashboard");
    } catch (err) {
      const totalTimeBeforeError = getCurrentTimeSpentMinutes();
      
      appInsights.trackException({
        exception: err,
        severityLevel: 3,
        properties: { 
          username: formData.username,
          timeToError: `${totalTimeBeforeError} minutes`,
        },
      });

      appInsights.trackException({
        name: "Login_Failed",
        properties: { 
          username: formData.username, 
          message: err.message,
          totalTimeBeforeFailure: `${totalTimeBeforeError} minutes`,
        },
      });

      setToastData({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Login Failed",
        message: "Login failed. Please check your credentials and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle forgot password
  const handleForgotPassword = async () => {
    const timeToForgotPassword = getCurrentTimeSpentMinutes();
    
    appInsights.trackEvent({
      name: "ForgotPassword_Clicked",
      properties: { 
        username: formData.username,
        timeToClick: `${timeToForgotPassword} minutes`,
      },
    });

    try {
      const result = await instance.loginPopup({
        scopes: resetPasswordConfig.scopes,
        authority: resetPasswordConfig.authority,
        redirectUri: resetPasswordConfig.redirectUri,
      });

      if (result.idToken) {
        appInsights.trackEvent({
          name: "PasswordReset_Success",
          properties: { username: formData.username },
        });

        setFormErrors({ username: "", password: "", global: "" });
        setToastData({
          type: CONSTANTS.TOAST_SUCCESS,
          heading: "Password Reset",
          message:
            "Password reset successful. Please log in with your new password.",
        });
      }
    } catch (err) {
      appInsights.trackException({
        exception: err,
        severityLevel: 3,
        properties: { username: formData.username },
      });

      appInsights.trackException({
        exception: err,
        severityLevel: 3,
        properties: { username: formData.username },
      });

      if (err.errorMessage && err.errorMessage.includes("AADB2C90118")) {
        setToastData({
          type: CONSTANTS.TOAST_WARNING,
          heading: "Password Reset",
          message: "Password reset cancelled.",
        });
      } else {
        setToastData({
          type: CONSTANTS.TOAST_ERROR,
          heading: "Password Reset",
          message: "Password reset failed. Try again.",
        });
      }
    }
  };

  return (
    <div className="d-flex">
      <div className="login-image-background" />
      <div className="d-flex flex-column justify-content-center align-items-center login-details-background p-4">
        <div className="d-flex justify-content-center flex-column p-5">
          <h2>Sign In</h2>
          <p className="fs-3">
            Please sign in to ROAR<sup>AI</sup> platform
          </p>

          {/* Username Field */}
          <div className="mb-3">
            <FormLabel label="Email" required />
            <FormControl
              type="text"
              placeholder="Email"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              height="45"
              borderRadius="5"
              autoComplete="off"
              isInvalid={!!formErrors.username}
              feedback={formErrors.username}
            />
          </div>

          {/* Password Field */}
          <div className="mb-3">
            <FormLabel label="Password" required />
            <div className="position-relative">
              <FormControl
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                height="45"
                borderRadius="5"
                autoComplete="off"
                isInvalid={!!formErrors.password}
                feedback={formErrors.password}
              />
              <i
                className={`bi ${
                  showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                }`}
                onClick={handlePasswordToggle}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: "1rem",
                  color: "#6c757d",
                }}
              />
            </div>

            {/* Forgot Password Link */}
            <div className="d-flex justify-content-end mt-2 mb-2">
              <a
                href="/forgot-password"
                className="forgot-password-label"
                onClick={(e) => {
                  e.preventDefault();
                  handleForgotPassword();
                }}
              >
                Forgot password
              </a>
            </div>
          </div>

          {/* Toast Notification */}
          {toastData && (
            <ToastNotification
              type={toastData.type}
              heading={toastData.heading}
              message={toastData.message}
            />
          )}

          <Button
            variant="primary"
            className="loginButton create-button"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Login;